import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Max wait before allowing sidebar mega menus even if an asset is slow. */
const MENU_READY_TIMEOUT_MS = 10000;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {number | null} */
  #megaMenuPanelHeightPx = null;

  /** Fallback when CSS custom properties are not yet available during early page load. */
  static #MEGA_MENU_PANEL_HEIGHT_FALLBACK = '26.5rem';

  /** Whether sidebar mega menus are allowed to open (assets preloaded). */
  #menuReady = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    void this.#prepareMenuForInteraction();
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerleave', this.#onSubmenuPointerLeave, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerleave', this.#onSubmenuPointerLeave, true);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#megaMenuPanelHeightPx = null;
    setHeaderMenuStyle();
    this.#syncOpenSubmenuHeightAfterLoad();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close when the pointer leaves the active submenu (capture phase).
   * @param {PointerEvent} event
   */
  #onSubmenuPointerLeave = (event) => {
    if (!(event.target instanceof Element)) return;

    const submenu = event.target.closest('[ref="submenu[]"]');
    if (!(submenu instanceof HTMLElement)) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const listItem = activeItem.closest('.menu-list__list-item');
    const menuitem = findMenuItem(listItem);
    if (menuitem !== activeItem) return;

    const related = event.relatedTarget;
    if (related instanceof Node && submenu.contains(related)) return;

    if (related instanceof Node && listItem?.contains(related)) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    if (this.dataset.productsDragging === 'true') return;

    if (activeLink.dataset.safetyBox !== 'true') {
      const listItem = activeLink.closest('.menu-list__list-item');
      const target = document.elementFromPoint(event.clientX, event.clientY);
      if (target instanceof Node && listItem && !listItem.contains(target)) {
        this.#deactivate();
        return;
      }
    }

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different top-level menu link.
   * @returns {boolean}
   */
  #isPointerOverDifferentMenuItem() {
    const target = document.elementFromPoint(this.#lastPointer.x, this.#lastPointer.y);
    if (!(target instanceof Element)) return false;

    const menuitem = target.closest('[ref="menuitem"]');
    return menuitem instanceof HTMLElement && menuitem !== this.#state.activeItem;
  }

  /**
   * Check if the pointer is still over the active submenu.
   * @returns {boolean}
   */
  #isPointerOverActiveSubmenu() {
    const submenu = findSubmenu(this.#state.activeItem);
    if (!(submenu instanceof HTMLElement)) return false;

    const target = document.elementFromPoint(this.#lastPointer.x, this.#lastPointer.y);
    return target instanceof Node && submenu.contains(target);
  }

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    if (this.dataset.productsDragging === 'true') return;

    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;

      const menuitem = target.closest('[ref="menuitem"]');
      if (menuitem instanceof HTMLElement && menuitem !== this.#state.activeItem) {
        this.activate({ target: menuitem });
        return;
      }

      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const menuListItem = item.closest('.menu-list__list-item');
    const isDefaultSlot = !(menuListItem instanceof HTMLElement && menuListItem.slot === 'overflow');

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (this.#isSidebarMegaMenuSubmenu(submenu) && !this.#menuReady) {
      return;
    }

    if (this.#menuReady && !this.hasAttribute('data-menu-ready')) {
      this.setAttribute('data-menu-ready', 'true');
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
      this.#stopPointerTracking(previouslyActiveItem);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      const isCompact = submenu.dataset.submenuLayout === 'compact';

      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Compact text dropdowns don't expand the full-width underlay
      if (!isCompact) {
        // Monitor DOM mutations to catch deferred content injection (from section hydration)
        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.#syncOpenSubmenuHeight(submenu, committedItem);
              }
              initMegaProductsCarousels(submenu);
              if (submenu.querySelector('.mega-menu__content-list--products .mega-menu__content-list-item')) {
                this.#cleanupMutationObserver();
              }
            });
          });
        });
        this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

        // Keep watching long enough for idle hydration + network
        setTimeout(() => {
          this.#cleanupMutationObserver();
          initMegaProductsCarousels(submenu);
        }, 4000);
      } else {
        initMegaProductsCarousels(submenu);
      }
    }

    let finalHeight = this.#measureSubmenuHeight(submenu);
    const isCompactSubmenu = submenu?.dataset.submenuLayout === 'compact';

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu || isCompactSubmenu) {
      // No content, or compact dropdown (self-sized, no full-width underlay)
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    if (submenu instanceof HTMLElement && submenu.querySelector('.mega-menu__sidebar')) {
      this.#scheduleSidebarMegaMenuHeightSync(submenu, item);
      requestAnimationFrame(() => {
        submenu.querySelector('.menu-list__submenu-inner')?.getBoundingClientRect();
      });
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    const leavingItem = findMenuItem(event.target);
    if (!leavingItem || leavingItem !== this.#state.activeItem) return;

    if (this.dataset.productsDragging === 'true') return;

    const related = event.relatedTarget;
    const listItem = leavingItem.closest('.menu-list__list-item');

    // Still within the same menu entry (e.g. link ↔ submenu, or li ↔ link)
    if (related instanceof Node && listItem?.contains(related)) {
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingToSubmenu = related instanceof Node && menu instanceof Node && menu.contains(related);
    const isMovingToOverflowMenu =
      related instanceof Node && related.parentElement?.matches('[slot="overflow"]');

    if (isMovingToSubmenu || isMovingToOverflowMenu) {
      return;
    }

    // Safari may omit relatedTarget when crossing into the submenu overlay
    if (!(related instanceof Node) && this.#isPointerOverActiveSubmenu()) {
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.dataset.productsDragging === 'true') return;

    const submenu = findSubmenu(item);
    if (submenu instanceof HTMLElement && submenu.matches(':hover')) {
      return;
    }

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * @param {HTMLElement | null | undefined} submenu
   * @returns {boolean}
   */
  #isSidebarMegaMenuSubmenu(submenu) {
    return submenu instanceof HTMLElement && Boolean(submenu.querySelector('.mega-menu__sidebar'));
  }

  /**
   * Preload mega menu assets before sidebar dropdowns are allowed to open.
   */
  #prepareMenuForInteraction = async () => {
    if (this.#menuReady) return;

    try {
      await Promise.race([
        this.#loadMenuAssets(),
        new Promise((resolve) => {
          setTimeout(resolve, MENU_READY_TIMEOUT_MS);
        }),
      ]);
    } finally {
      this.#markMenuReady();
    }
  };

  #loadMenuAssets = async () => {
    await new Promise((resolve) => onDocumentLoaded(resolve));

    await this.#waitForHeaderHydration();

    this.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));

    await Promise.all([
      document.fonts?.ready ?? Promise.resolve(),
      new Promise((resolve) => {
        if (document.readyState === 'complete') resolve(undefined);
        else window.addEventListener('load', () => resolve(undefined), { once: true });
      }),
    ]);

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    await this.#waitForImages(this);

    initMegaProductsCarousels(this);
  };

  /**
   * Featured-product mega menus hydrate after first paint. Wait so hover
   * does not open an empty white panel.
   * @returns {Promise<void>}
   */
  #waitForHeaderHydration() {
    const section =
      this.closest('.shopify-section.header-section') ||
      document.querySelector('.shopify-section.header-section');

    if (!(section instanceof HTMLElement)) return Promise.resolve();
    if (section.dataset.hydrated === 'true') return Promise.resolve();

    const hasFeaturedCarousels = Boolean(this.querySelector('[data-mega-products-carousel]'));
    if (!hasFeaturedCarousels) return Promise.resolve();

    if (this.querySelector('.mega-menu__content-list--products .mega-menu__content-list-item')) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        observer.disconnect();
        resolve();
      };

      const observer = new MutationObserver(() => {
        if (
          section.dataset.hydrated === 'true' ||
          this.querySelector('.mega-menu__content-list--products .mega-menu__content-list-item')
        ) {
          done();
        }
      });

      observer.observe(section, {
        attributes: true,
        attributeFilter: ['data-hydrated'],
        childList: true,
        subtree: true,
      });
    });
  }

  /**
   * @param {ParentNode} root
   */
  #waitForImages(root) {
    const images = root.querySelectorAll('img');
    if (!images.length) return Promise.resolve();

    return Promise.all(
      Array.from(images).map(
        (image) =>
          new Promise((resolve) => {
            if (image.complete) {
              resolve(undefined);
              return;
            }

            image.addEventListener('load', () => resolve(undefined), { once: true });
            image.addEventListener('error', () => resolve(undefined), { once: true });
          })
      )
    );
  }

  #markMenuReady() {
    if (this.#menuReady) return;

    this.#menuReady = true;
    this.setAttribute('data-menu-ready', 'true');

    this.querySelectorAll('.menu-list__submenu:has(.mega-menu__sidebar)').forEach((submenu) => {
      submenu.getBoundingClientRect();
    });

    this.#syncOpenSubmenuHeightAfterLoad();
    this.#openHoveredSidebarMegaMenu();
  }

  #openHoveredSidebarMegaMenu() {
    const hovered = this.querySelector('[ref="menuitem"]:hover');
    if (!(hovered instanceof HTMLElement) || hovered === this.#state.activeItem) return;

    const submenu = findSubmenu(hovered);
    if (!this.#isSidebarMegaMenuSubmenu(submenu)) return;

    this.activate(/** @type {PointerEvent} */ ({ target: hovered }));
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Fixed panel height for sidebar mega menus so every dropdown opens at the same size.
   * Always reads the declared CSS token — never measures inner.offsetHeight, which is
   * unreliable while images/fonts/styles are still loading.
   * @param {HTMLElement | null | undefined} submenu
   * @returns {number | null}
   */
  #resolveMegaMenuPanelHeight(submenu) {
    if (!(submenu instanceof HTMLElement) || !submenu.querySelector('.mega-menu__sidebar')) {
      return null;
    }

    if (this.#megaMenuPanelHeightPx) {
      return this.#megaMenuPanelHeightPx;
    }

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (inner instanceof HTMLElement && inner.offsetHeight > 0) {
      this.#megaMenuPanelHeightPx = inner.offsetHeight;
      return this.#megaMenuPanelHeightPx;
    }

    const panelHeight = getComputedStyle(this).getPropertyValue('--mega-menu-panel-height').trim();
    if (!panelHeight) return null;

    const probe = document.createElement('div');
    probe.style.visibility = 'hidden';
    probe.style.position = 'absolute';
    probe.style.pointerEvents = 'none';
    probe.style.height = panelHeight;
    this.appendChild(probe);
    this.#megaMenuPanelHeightPx = probe.offsetHeight;
    probe.remove();

    return this.#megaMenuPanelHeightPx;
  }

  /**
   * Write header height vars for the currently open submenu.
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   */
  #syncOpenSubmenuHeight(submenu, item) {
    if (!this.headerComponent || this.#state.activeItem !== item) return;

    const height = this.#measureSubmenuHeight(submenu);
    if (!height) return;

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent.style.setProperty('--submenu-height', `${height}px`);
    this.#setFullOpenHeaderHeight(height, headerVisibleHeight);
  }

  /**
   * Re-sync sidebar mega menu height after layout, fonts, and images settle.
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   */
  #scheduleSidebarMegaMenuHeightSync(submenu, item) {
    const sync = () => {
      if (this.#state.activeItem !== item) return;
      this.#megaMenuPanelHeightPx = null;
      this.#syncOpenSubmenuHeight(submenu, item);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(sync);
    });

    document.fonts?.ready.then(sync);

    submenu.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', sync, { once: true });
      image.addEventListener('error', sync, { once: true });
    });
  }

  #syncOpenSubmenuHeightAfterLoad = () => {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const submenu = findSubmenu(activeItem);
    if (submenu instanceof HTMLElement && submenu.querySelector('.mega-menu__sidebar')) {
      this.#scheduleSidebarMegaMenuHeightSync(submenu, activeItem);
    }
  };

  /**
   * @param {HTMLElement | null | undefined} submenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu) {
    if (!(submenu instanceof HTMLElement)) return 0;
    return this.#resolveMegaMenuPanelHeight(submenu) ?? submenu.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Enable mouse drag-to-scroll on a horizontal track.
 * @param {HTMLElement} track
 */
function attachMegaProductsDragScroll(track) {
  if (track.dataset.dragScrollReady === 'true') return;
  track.dataset.dragScrollReady = 'true';

  const headerMenu = track.closest('header-menu');
  const DRAG_THRESHOLD_PX = 6;
  const drag = {
    pending: false,
    active: false,
    moved: false,
    startX: 0,
    startLeft: 0,
    pointerId: null,
  };

  const setDragging = (isDragging) => {
    if (headerMenu instanceof HTMLElement) {
      if (isDragging) {
        headerMenu.dataset.productsDragging = 'true';
      } else {
        delete headerMenu.dataset.productsDragging;
      }
    }
  };

  const endDrag = () => {
    if (!drag.pending && !drag.active) return;

    const didDrag = drag.moved;
    drag.pending = false;
    drag.active = false;
    track.classList.remove('is-dragging');
    setDragging(false);

    if (headerMenu instanceof HTMLElement) {
      const activeLink = headerMenu.querySelector('[ref="menuitem"][aria-expanded="true"]');
      if (activeLink instanceof HTMLElement) {
        delete activeLink.dataset.safetyBox;
      }
    }

    if (drag.pointerId != null) {
      try {
        track.releasePointerCapture(drag.pointerId);
      } catch {
        /* ignore */
      }
      drag.pointerId = null;
    }

    // Only suppress click after a real drag, so product links still work
    if (didDrag) {
      track.dataset.suppressClick = '1';
      window.setTimeout(() => {
        delete track.dataset.suppressClick;
      }, 0);
    }
  };

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (track.scrollWidth <= track.clientWidth) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, input, label')) return;

    // Defer capture until the pointer actually moves — plain clicks must reach product links
    drag.pending = true;
    drag.active = false;
    drag.moved = false;
    drag.startX = event.clientX;
    drag.startLeft = track.scrollLeft;
    drag.pointerId = event.pointerId;
  });

  track.addEventListener('pointermove', (event) => {
    if (!drag.pending && !drag.active) return;

    const delta = event.clientX - drag.startX;

    if (!drag.active) {
      if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;

      drag.active = true;
      drag.moved = true;
      track.classList.add('is-dragging');
      setDragging(true);
      try {
        track.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }

    track.scrollLeft = drag.startLeft - delta;
  });

  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
  track.addEventListener('lostpointercapture', endDrag);

  track.addEventListener(
    'click',
    (event) => {
      if (track.dataset.suppressClick === '1') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );
}

/**
 * Mega menu featured-products horizontal carousel (scrollbar + arrows).
 * @param {ParentNode} [root]
 */
function initMegaProductsCarousels(root = document) {
  root.querySelectorAll('[data-mega-products-carousel]').forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) return;

    const track = carousel.querySelector('[data-mega-products-track]');
    if (!(track instanceof HTMLElement)) return;

    if (carousel.dataset.carouselReady !== 'true') {
      carousel.dataset.carouselReady = 'true';

      const prev = carousel.querySelector('[data-mega-products-prev]');
      const next = carousel.querySelector('[data-mega-products-next]');

      const step = () => Math.max(track.clientWidth * 0.85, 160);

      prev?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });

      next?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });

      track.addEventListener('scroll', () => updateMegaProductsCarousel(carousel), { passive: true });
      attachMegaProductsDragScroll(track);
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(() => updateMegaProductsCarousel(carousel)).observe(track);
      }
    }

    updateMegaProductsCarousel(carousel);
  });
}

/**
 * @param {HTMLElement} carousel
 */
function updateMegaProductsCarousel(carousel) {
  const track = carousel.querySelector('[data-mega-products-track]');
  const thumb = carousel.querySelector('[data-mega-products-thumb]');
  const prev = carousel.querySelector('[data-mega-products-prev]');
  const next = carousel.querySelector('[data-mega-products-next]');
  if (!(track instanceof HTMLElement)) return;

  const maxScroll = track.scrollWidth - track.clientWidth;
  const scrollable = maxScroll > 4;
  carousel.dataset.scrollable = scrollable ? 'true' : 'false';

  if (thumb instanceof HTMLElement) {
    if (!scrollable) {
      thumb.style.width = '100%';
      thumb.style.left = '0%';
    } else {
      const thumbWidthPct = Math.min(100, Math.max(12, (track.clientWidth / track.scrollWidth) * 100));
      const thumbLeftPct = (track.scrollLeft / maxScroll) * (100 - thumbWidthPct);
      thumb.style.width = `${thumbWidthPct}%`;
      thumb.style.left = `${thumbLeftPct}%`;
    }
  }

  if (prev instanceof HTMLButtonElement) {
    prev.disabled = !scrollable || track.scrollLeft <= 2;
  }
  if (next instanceof HTMLButtonElement) {
    next.disabled = !scrollable || track.scrollLeft >= maxScroll - 2;
  }
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element;
  }

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const closestMenuItem = element.closest('[ref="menuitem"]');
  if (closestMenuItem instanceof HTMLElement) {
    return closestMenuItem;
  }

  const listItem = element.closest('.menu-list__list-item');
  const listMenuItem = listItem?.querySelector('[ref="menuitem"]');
  if (listMenuItem instanceof HTMLElement) {
    return listMenuItem;
  }

  const nestedMenuItem = element.querySelector('[ref="menuitem"]');
  return nestedMenuItem instanceof HTMLElement ? nestedMenuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
